'use client';

import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import {
  faEllipsisV,
  faCheck,
  faArrowUp,
  faArrowDown,
} from '@fortawesome/pro-solid-svg-icons';

type SortOrder = 'asc' | 'desc';

interface PulseColumnMenuProps {
  columnId: string;
  isOpen?: boolean;
  onToggle?: (e: React.MouseEvent) => void;
  sortSection?: {
    isActive: boolean;
    order: SortOrder;
    onSort: (order: SortOrder) => void;
  };
  customSectionComponents?: React.ReactNode[];
}

const DROPDOWN_WIDTH = 240;
const DROPDOWN_OFFSET = 4;

const PulseColumnMenu: React.FC<PulseColumnMenuProps> = ({
  isOpen,
  onToggle,
  sortSection,
  customSectionComponents = [],
}) => {
  // Determine if we should show any sections after the sort section
  const hasSortSection = !!sortSection;
  const hasComponentSections = customSectionComponents.length > 0;

  const buttonRef = useRef<HTMLButtonElement>(null);
  const portalRef = useRef<HTMLDivElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0, width: DROPDOWN_WIDTH });

  // Update dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      // Calculate preferred left position (align right edge of dropdown to button)
      let left = rect.right - DROPDOWN_WIDTH;
      let width = DROPDOWN_WIDTH;
      // If dropdown would go off left edge, shrink it to fit from left edge to button
      if (left < DROPDOWN_OFFSET) {
        left = DROPDOWN_OFFSET;
        width = rect.right - DROPDOWN_OFFSET;
      }
      setDropdownPosition({
        top: rect.bottom + DROPDOWN_OFFSET,
        left,
        width,
      });
    }
  }, [isOpen]);

  // Handle clicks outside to close
  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as Node;
      const isClickInButton = buttonRef.current?.contains(target);
      const isClickInDropdown = portalRef.current?.contains(target);

      if (!isClickInButton && !isClickInDropdown && isOpen) {
        onToggle?.(event as any);
      }
    },
    [isOpen, onToggle]
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, handleClickOutside]);

  return (
    <>
      <button
        ref={buttonRef}
        className="absolute right-2 top-1/2 -translate-y-1/2 transition-all w-6 h-6 flex items-center justify-center hover:bg-neutral-200 rounded-full opacity-0 group-hover:opacity-100"
        onClick={onToggle}
        aria-label="Column menu"
      >
        <Icon icon={faEllipsisV} className="text-xs text-neutral-600" />
      </button>

      {isOpen &&
        createPortal(
          <div
            ref={portalRef}
            className="fixed bg-white shadow-lg rounded-lg border border-neutral-200 py-1 z-[9999]"
            style={{
              top: `${dropdownPosition.top}px`,
              left: `${dropdownPosition.left}px`,
              width: `${dropdownPosition.width}px`,
            }}
            onMouseEnter={(e) => e.stopPropagation()}
          >
            {/* Sort Section */}
            {sortSection && (
              <>
                <div className="px-3 py-2 text-xs font-semibold text-neutral-500 uppercase tracking-wide">
                  Sort
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    sortSection.onSort('asc');
                  }}
                  className={`w-full px-4 py-2 text-sm text-left hover:bg-neutral-200 transition-colors flex items-center justify-between ${
                    sortSection.isActive && sortSection.order === 'asc'
                      ? 'bg-sky-50 text-sky-600 font-medium'
                      : ''
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Icon icon={faArrowUp} className="text-xs" />
                    Sort Ascending
                  </span>
                  {sortSection.isActive && sortSection.order === 'asc' && (
                    <Icon icon={faCheck} className="text-xs" />
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    sortSection.onSort('desc');
                  }}
                  className={`w-full px-4 py-2 text-sm text-left hover:bg-neutral-200 transition-colors flex items-center justify-between ${
                    sortSection.isActive && sortSection.order === 'desc'
                      ? 'bg-sky-50 text-sky-600 font-medium'
                      : ''
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <Icon icon={faArrowDown} className="text-xs" />
                    Sort Descending
                  </span>
                  {sortSection.isActive && sortSection.order === 'desc' && (
                    <Icon icon={faCheck} className="text-xs" />
                  )}
                </button>
              </>
            )}

            {/* Component-based Custom Sections */}
            {hasComponentSections &&
              customSectionComponents.map((component, index) => (
                <React.Fragment key={index}>
                  {/* Divider - show if there's content before this section */}
                  {(hasSortSection || index > 0) && (
                    <div className="my-1 border-t border-neutral-200" />
                  )}
                  {component}
                </React.Fragment>
              ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default PulseColumnMenu;
